import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiConfig } from "../config/authConfig";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Card,
  Badge,
  DataTable,
  Modal,
  Input,
  Select,
  Textarea,
} from "../components/ui";

const TIPO_UTENZA_OPTIONS = {
  100000000: {
    label: "Energia elettrica",
    icon: "⚡",
    color: "text-yellow-600",
  },
  100000001: { label: "Gas naturale", icon: "🔥", color: "text-orange-600" },
  100000002: { label: "Acqua", icon: "💧", color: "text-blue-600" },
  100000003: { label: "Teleriscaldamento", icon: "🌡️", color: "text-red-600" },
  100000004: {
    label: "Telecomunicazioni",
    icon: "📡",
    color: "text-purple-600",
  },
  100000005: { label: "Rifiuti", icon: "♻️", color: "text-green-600" },
  100000007: {
    label: "Rifiuti non speciali",
    icon: "♻️",
    color: "text-green-600",
  },
  100000008: {
    label: "Rifiuti speciali",
    icon: "☣️",
    color: "text-amber-700",
  },
  100000006: { label: "Altro", icon: "📦", color: "text-gray-600" },
};

const STATO_PAGAMENTO_OPTIONS = {
  100000000: { label: "Da pagare", variant: "warning" },
  100000001: { label: "Pagata", variant: "success" },
  100000002: { label: "Scaduta", variant: "danger" },
  100000003: { label: "In contestazione", variant: "info" },
};

function ConsumiUtenzeListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, apiRequest, getAccessToken } = useAuth();
  const { showToast } = useToast();

  const [consumi, setConsumi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filtri da URL
  const rsaIdFilter = searchParams.get("rsaId") || "";
  const tipoFilter = searchParams.get("tipoutenza") || "";
  const statoFilter = searchParams.get("stato") || "";
  const annoFilter =
    searchParams.get("anno") || new Date().getFullYear().toString();
  const orfaneFilter = searchParams.get("orfane") === "1";

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canCreate = isAdmin || isRspp;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Carica lista RSA per filtro
        const rsaData = await apiRequest("/rsa");
        setRsaList(rsaData.value || []);

        // Costruisci query params
        const params = new URLSearchParams();
        if (rsaIdFilter) params.append("rsaId", rsaIdFilter);
        if (tipoFilter) params.append("tipoutenza", tipoFilter);
        if (statoFilter) params.append("statoPagamento", statoFilter);
        if (annoFilter) params.append("anno", annoFilter);
        if (orfaneFilter) params.append("orfane", "1");

        // Carica consumi
        const data = await apiRequest(`/consumi-utenze?${params.toString()}`);
        const all = data.value || [];
        setConsumi(orfaneFilter ? all.filter((c) => !c.contatoreId) : all);

        // Carica statistiche
        const statsParams = new URLSearchParams();
        if (annoFilter) statsParams.append("anno", annoFilter);
        const statsData = await apiRequest(
          `/consumi-utenze/stats/overview?${statsParams.toString()}`,
        ).catch(() => null);
        setStats(statsData);
      } catch (error) {
        console.error("Error fetching consumi:", error);
        showToast("Errore nel caricamento dei consumi", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    apiRequest,
    rsaIdFilter,
    tipoFilter,
    statoFilter,
    annoFilter,
    showToast,
    refreshKey,
  ]);

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT");
  };

  // --- PDF Upload Modal state ---
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPhase, setPdfPhase] = useState("upload"); // "upload" | "review" | "summary"
  const [pdfFiles, setPdfFiles] = useState([]);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [bulkResults, setBulkResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pdfResult, setPdfResult] = useState(null);
  const [pdfForm, setPdfForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [immobiliList, setImmobiliList] = useState([]);
  const [contatoriList, setContatoriList] = useState([]);

  // --- Excel Export state ---
  const currentYear = new Date().getFullYear();
  const [exportDa, setExportDa] = useState(`${currentYear}-01-01`);
  const [exportA, setExportA] = useState(`${currentYear}-12-31`);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingMensile, setExportingMensile] = useState(false);
  // L'export mensile e' quello che finisce in mano al proprietario dell'immobile,
  // che ragiona per indirizzo e non per RSA: qui serve poter scendere al singolo immobile.
  const [exportImmobileId, setExportImmobileId] = useState("");
  const [exportImmobiliList, setExportImmobiliList] = useState([]);

  useEffect(() => {
    setExportImmobileId("");
    if (!rsaIdFilter) {
      setExportImmobiliList([]);
      return;
    }
    let annullato = false;
    apiRequest(`/immobili?rsaId=${rsaIdFilter}`)
      .then((res) => {
        if (!annullato) setExportImmobiliList(res?.value || []);
      })
      .catch(() => {
        if (!annullato) setExportImmobiliList([]);
      });
    return () => {
      annullato = true;
    };
  }, [apiRequest, rsaIdFilter]);

  const handleExportExcel = async () => {
    if (!exportDa || !exportA) {
      showToast("Seleziona date inizio e fine per l'export", "error");
      return;
    }
    setExporting(true);
    try {
      const token = await getAccessToken();
      const exportParams = new URLSearchParams({
        dataInizio: exportDa,
        dataFine: exportA,
      });
      if (rsaIdFilter) exportParams.set("rsaId", rsaIdFilter);
      if (tipoFilter) exportParams.set("tipoutenza", tipoFilter);
      if (statoFilter) exportParams.set("statoPagamento", statoFilter);
      const url = `${apiConfig.baseUrl}/consumi-utenze/export-excel?${exportParams.toString()}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Errore ${response.status}`);
      }
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Consumi_Utenze_${exportDa}_${exportA}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      showToast("File Excel scaricato", "success");
    } catch (error) {
      console.error("Export error:", error);
      showToast(`Errore export: ${error.message}`, "error");
    } finally {
      setExporting(false);
    }
  };

  /**
   * Export "consumi per mese": la tabella tipologia x mese che i proprietari degli immobili
   * chiedono per i loro obiettivi di sostenibilita'. Diverso da handleExportExcel, che resta
   * l'estratto analitico riga-per-bolletta a uso interno.
   */
  const handleExportConsumiMensili = async () => {
    if (!exportDa || !exportA) {
      showToast("Seleziona date inizio e fine per l'export", "error");
      return;
    }
    setExportingMensile(true);
    try {
      const token = await getAccessToken();
      const exportParams = new URLSearchParams({
        dataInizio: exportDa,
        dataFine: exportA,
      });
      if (rsaIdFilter) exportParams.set("rsaId", rsaIdFilter);
      if (exportImmobileId) exportParams.set("immobileId", exportImmobileId);
      const url = `${apiConfig.baseUrl}/consumi-utenze/export-consumi-mensili?${exportParams.toString()}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Errore ${response.status}`);
      }
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Consumi_Mensili_${exportDa}_${exportA}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      showToast("Report consumi per mese scaricato", "success");
    } catch (error) {
      console.error("Export consumi mensili error:", error);
      showToast(`Errore export: ${error.message}`, "error");
    } finally {
      setExportingMensile(false);
    }
  };

  const handleExportPdf = async () => {
    if (!exportDa || !exportA) {
      showToast("Seleziona date inizio e fine per l'export", "error");
      return;
    }
    setExportingPdf(true);
    try {
      const token = await getAccessToken();
      const exportParams = new URLSearchParams({
        dataInizio: exportDa,
        dataFine: exportA,
      });
      if (rsaIdFilter) exportParams.set("rsaId", rsaIdFilter);
      if (tipoFilter) exportParams.set("tipoutenza", tipoFilter);
      if (statoFilter) exportParams.set("statoPagamento", statoFilter);
      const url = `${apiConfig.baseUrl}/consumi-utenze/export-pdf?${exportParams.toString()}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Errore ${response.status}`);
      }
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Bollette_PDF_${exportDa}_${exportA}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      showToast("File ZIP con PDF scaricato", "success");
    } catch (error) {
      console.error("Export PDF error:", error);
      showToast(`Errore export PDF: ${error.message}`, "error");
    } finally {
      setExportingPdf(false);
    }
  };

  const openPdfModal = () => {
    setPdfPhase("upload");
    setPdfFiles([]);
    setCurrentPdfIndex(0);
    setBulkResults([]);
    setPdfResult(null);
    setPdfForm({});
    setShowPdfModal(true);
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    setPdfFiles([]);
    setCurrentPdfIndex(0);
    setBulkResults([]);
    setPdfResult(null);
    setPdfForm({});
    setRefreshKey((k) => k + 1);
  };

  const processNextPdf = async (index) => {
    if (index >= pdfFiles.length) {
      setPdfPhase("summary");
      return;
    }
    setCurrentPdfIndex(index);
    setUploading(true);
    try {
      const file = pdfFiles[index];
      const fd = new FormData();
      fd.append("file", file);
      const result = await apiRequest("/consumi-utenze/upload-pdf", {
        method: "POST",
        body: fd,
        headers: {},
      });
      setPdfResult(result);

      const { parsed, contatore } = result;
      // For waste invoices, use codiceCliente as pod identifier
      const podValue =
        parsed.pod || parsed.codiceUtenza || parsed.codiceCliente || "";
      // Build note with waste details if available
      const noteValue = parsed.wasteNote || "";
      setPdfForm({
        tipoutenza:
          parsed.tipoutenza?.toString() ||
          contatore?.tipoutenza?.toString() ||
          "",
        periodoInizio: parsed.periodoInizio || "",
        periodoFine: parsed.periodoFine || "",
        dataEmissione: parsed.dataEmissione || "",
        dataScadenza: parsed.dataScadenza || "",
        importoTotale: parsed.importoTotale ?? "",
        importoEnergia: parsed.importoEnergia ?? "",
        importoRete: parsed.importoRete ?? "",
        importoImposte: parsed.importoImposte ?? "",
        consumoQuantita: parsed.consumoQuantita ?? "",
        consumoUnita: parsed.consumoUnita || "",
        pod: podValue,
        numeroFattura: parsed.numeroFattura || "",
        statoPagamento: "100000000",
        note: noteValue,
        rsaId: contatore?.rsaId || "",
        immobileId: contatore?.immobileId || "",
        contatoreId: contatore?.id || "",
        fornitoreId: contatore?.fornitoreId || "",
        // Dettaglio costi (from PDF parsing)
        nomeOfferta: parsed.nomeOfferta || "",
        prezzoUnitario: parsed.prezzoUnitario ?? "",
        potenzaImpegnata: parsed.potenzaImpegnata ?? "",
        quotaFissa: parsed.quotaFissa ?? "",
        quotaPotenza: parsed.quotaPotenza ?? "",
        spesaTrasporto: parsed.spesaTrasporto ?? "",
        spesaOneri: parsed.spesaOneri ?? "",
        accise: parsed.accise ?? "",
        iva: parsed.iva ?? "",
        consumoF1: parsed.consumoF1 ?? "",
        consumoF2: parsed.consumoF2 ?? "",
        consumoF3: parsed.consumoF3 ?? "",
        prezzoF1: parsed.prezzoF1 ?? "",
        prezzoF2: parsed.prezzoF2 ?? "",
        prezzoF3: parsed.prezzoF3 ?? "",
        dispacciamento: parsed.dispacciamento ?? "",
        sbilanciamento: parsed.sbilanciamento ?? "",
        spreadAlfa: parsed.spreadAlfa ?? "",
        indicePsv: parsed.indicePsv ?? "",
        dettaglioJson: parsed.dettaglioJson || "",
      });

      // Load fornitori
      const fornData = await apiRequest("/fornitori");
      setFornitoriList(fornData.value || []);

      // Load immobili/contatori if RSA resolved
      if (contatore?.rsaId) {
        const [immData, contData] = await Promise.all([
          apiRequest(`/immobili?rsaId=${contatore.rsaId}`),
          apiRequest(`/contatori?rsaId=${contatore.rsaId}`),
        ]);
        setImmobiliList(immData.value || []);
        setContatoriList(contData.value || []);
      }

      setPdfPhase("review");
    } catch (error) {
      console.error("PDF parse error:", error);
      showToast(
        `Errore analisi "${pdfFiles[index]?.name}": ${error.message || ""}`,
        "error",
      );
      // On parse error, add to results and skip to next
      setBulkResults((prev) => [
        ...prev,
        {
          fileName: pdfFiles[index]?.name,
          status: "error",
          error: error.message,
        },
      ]);
      await processNextPdf(index + 1);
    } finally {
      setUploading(false);
    }
  };

  const handlePdfUpload = async () => {
    if (pdfFiles.length === 0) return;
    await processNextPdf(0);
  };

  const handlePdfSkip = async () => {
    const fileName = pdfFiles[currentPdfIndex]?.name;
    setBulkResults((prev) => [...prev, { fileName, status: "skipped" }]);
    await processNextPdf(currentPdfIndex + 1);
  };

  const handlePdfFormChange = (key, value) => {
    setPdfForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePdfRsaChange = async (rsaId) => {
    setPdfForm((prev) => ({
      ...prev,
      rsaId,
      immobileId: "",
      contatoreId: "",
    }));
    setImmobiliList([]);
    setContatoriList([]);
    if (rsaId) {
      try {
        const [immData, contData] = await Promise.all([
          apiRequest(`/immobili?rsaId=${rsaId}`),
          apiRequest(`/contatori?rsaId=${rsaId}`),
        ]);
        setImmobiliList(immData.value || []);
        setContatoriList(contData.value || []);
      } catch (e) {
        console.error("Error loading RSA data:", e);
      }
    }
  };

  const handlePdfSave = async () => {
    if (!pdfForm.rsaId || !pdfForm.fornitoreId) {
      showToast("RSA e Fornitore sono obbligatori", "error");
      return;
    }
    if (!pdfForm.importoTotale) {
      showToast("Importo totale obbligatorio", "error");
      return;
    }
    const currentFile = pdfFiles[currentPdfIndex];
    const fileName = currentFile?.name;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", currentFile);
      fd.append("rsaId", pdfForm.rsaId);
      fd.append("fornitoreId", pdfForm.fornitoreId);
      if (pdfForm.tipoutenza) fd.append("tipoutenza", pdfForm.tipoutenza);
      if (pdfForm.periodoInizio)
        fd.append("periodoInizio", pdfForm.periodoInizio);
      if (pdfForm.periodoFine) fd.append("periodoFine", pdfForm.periodoFine);
      if (pdfForm.dataEmissione)
        fd.append("dataEmissione", pdfForm.dataEmissione);
      if (pdfForm.dataScadenza) fd.append("dataScadenza", pdfForm.dataScadenza);
      if (pdfForm.importoTotale)
        fd.append("importoTotale", pdfForm.importoTotale);
      if (pdfForm.importoEnergia)
        fd.append("importoEnergia", pdfForm.importoEnergia);
      if (pdfForm.importoRete) fd.append("importoRete", pdfForm.importoRete);
      if (pdfForm.importoImposte)
        fd.append("importoImposte", pdfForm.importoImposte);
      if (pdfForm.consumoQuantita)
        fd.append("consumoQuantita", pdfForm.consumoQuantita);
      if (pdfForm.consumoUnita) fd.append("consumoUnita", pdfForm.consumoUnita);
      if (pdfForm.pod) fd.append("pod", pdfForm.pod);
      if (pdfForm.numeroFattura)
        fd.append("numeroFattura", pdfForm.numeroFattura);
      fd.append("statoPagamento", pdfForm.statoPagamento || "100000000");
      if (pdfForm.note) fd.append("note", pdfForm.note);
      if (pdfForm.immobileId) fd.append("immobileId", pdfForm.immobileId);
      if (pdfForm.contatoreId) fd.append("contatoreId", pdfForm.contatoreId);
      fd.append(
        "confidenzaEstrazione",
        pdfResult?.confidence?.toString() || "0",
      );

      // Dettaglio costi
      const detailFields = [
        "nomeOfferta",
        "prezzoUnitario",
        "potenzaImpegnata",
        "quotaFissa",
        "quotaPotenza",
        "spesaTrasporto",
        "spesaOneri",
        "accise",
        "iva",
        "consumoF1",
        "consumoF2",
        "consumoF3",
        "prezzoF1",
        "prezzoF2",
        "prezzoF3",
        "dispacciamento",
        "sbilanciamento",
        "spreadAlfa",
        "indicePsv",
        "dettaglioJson",
      ];
      for (const f of detailFields) {
        if (pdfForm[f] !== "" && pdfForm[f] != null) {
          fd.append(f, String(pdfForm[f]));
        }
      }

      await apiRequest("/consumi-utenze/save-with-pdf", {
        method: "POST",
        body: fd,
        headers: {},
      });

      setBulkResults((prev) => [...prev, { fileName, status: "saved" }]);
      setSaving(false);
      await processNextPdf(currentPdfIndex + 1);
    } catch (error) {
      if (error.status === 409 && error.data?.existing) {
        const existing = error.data.existing;
        const periodo =
          existing.phc_periodofatturazioneinizio &&
          existing.phc_periodofatturazionefine
            ? `${formatDate(existing.phc_periodofatturazioneinizio)} - ${formatDate(existing.phc_periodofatturazionefine)}`
            : null;
        const detailParts = [
          existing.phc_numerofattura
            ? `n. ${existing.phc_numerofattura}`
            : null,
          periodo,
          existing.phc_importototale != null
            ? `€ ${Number(existing.phc_importototale).toFixed(2)}`
            : null,
        ].filter(Boolean);
        const detailMsg = detailParts.length
          ? ` (${detailParts.join(", ")})`
          : "";
        showToast(`"${fileName}" già presente: fattura${detailMsg}`, "warning");
        setBulkResults((prev) => [
          ...prev,
          {
            fileName,
            status: "duplicate",
            error: error.data.warning || "Bolletta già presente",
            existing,
          },
        ]);
      } else {
        console.error("Error saving bolletta:", error);
        showToast(`Errore salvataggio "${fileName}"`, "error");
        setBulkResults((prev) => [
          ...prev,
          { fileName, status: "error", error: error.message },
        ]);
      }
      setSaving(false);
      await processNextPdf(currentPdfIndex + 1);
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence == null) return null;
    if (confidence >= 70)
      return <Badge variant="success">{confidence}% confidenza</Badge>;
    if (confidence >= 40)
      return <Badge variant="warning">{confidence}% confidenza</Badge>;
    return <Badge variant="danger">{confidence}% confidenza</Badge>;
  };

  const columns = [
    {
      key: "tipo",
      label: "Tipo",
      render: (row) => {
        const tipo = TIPO_UTENZA_OPTIONS[row.tipoutenza];
        return (
          <span className={tipo?.color || ""}>
            {tipo?.icon || "📦"} {tipo?.label || "N/D"}
          </span>
        );
      },
    },
    {
      key: "nome",
      label: "Bolletta",
      render: (row) => (
        <Link
          to={`/consumi-utenze/${row.id}`}
          className="text-blue-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "periodo",
      label: "Periodo",
      render: (row) => {
        if (!row.periodoInizio && !row.periodoFine) return "-";
        return `${formatDate(row.periodoInizio)} - ${formatDate(row.periodoFine)}`;
      },
    },
    {
      key: "importo",
      label: "Importo",
      render: (row) => (
        <span className="font-semibold">
          {formatCurrency(row.importoTotale)}
        </span>
      ),
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => row.rsaNome || "-",
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) => row.fornitoreNome || "-",
    },
    {
      key: "origine",
      label: "Origine",
      render: (row) => (
        <div className="flex flex-col gap-1">
          {row.origineAutomatica ? (
            <Badge variant="info">Auto</Badge>
          ) : (
            <Badge variant="default">Manuale</Badge>
          )}
          {!row.contatoreId && (
            <Badge variant="warning" title="Nessun contatore associato">
              Senza contatore
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "pdf",
      label: "PDF",
      render: (row) =>
        row.documentoUrl ? (
          <a
            href={row.documentoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            Apri
          </a>
        ) : null,
    },
  ];

  // Anni per filtro (ultimi 5 anni)
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Bollette e Consumi
          </h1>
          <p className="text-gray-500 mt-1">
            Gestione bollette utenze e tracking consumi
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/consumi-utenze/analisi")}
          >
            Grafici e Analisi
          </Button>
          <Button variant="outline" onClick={() => navigate("/contatori")}>
            Registro Contatori
          </Button>
          {canCreate && (
            <Button variant="outline" onClick={openPdfModal}>
              Carica PDF
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => navigate("/consumi-utenze/nuovo")}>
              + Nuova Bolletta
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-sm text-gray-500">Totale bollette</p>
            <p className="text-2xl font-bold">{stats.totaleBollette}</p>
            <p className="text-sm text-gray-500">
              {formatCurrency(stats.totaleImporto)}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">Da pagare</p>
            <p className="text-2xl font-bold text-amber-600">
              {stats.daPagare}
            </p>
            <p className="text-sm text-amber-600">
              {formatCurrency(stats.importoDaPagare)}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">Scadute</p>
            <p className="text-2xl font-bold text-red-600">{stats.scadute}</p>
            <p className="text-sm text-red-600">
              {formatCurrency(stats.importoScadute)}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">Per tipo utenza</p>
            <div className="space-y-1 mt-1">
              {(stats.perTipoUtenza || []).map((t) => (
                <div key={t.tipo} className="flex justify-between text-sm">
                  <span>
                    {TIPO_UTENZA_OPTIONS[t.tipo]?.icon} {t.label}
                  </span>
                  <span className="font-medium">
                    {formatCurrency(t.importoTotale)}
                  </span>
                </div>
              ))}
            </div>
          </Card>
          {(() => {
            const orfaneCount = consumi.filter((c) => !c.contatoreId).length;
            return orfaneCount > 0 ? (
              <Card
                className="p-4 border-orange-300 bg-orange-50 cursor-pointer"
                onClick={() =>
                  handleFilterChange("orfane", orfaneFilter ? "" : "1")
                }
              >
                <p className="text-sm text-orange-600 font-medium">
                  Bollette senza contatore
                </p>
                <p className="text-2xl font-bold text-orange-700">
                  {orfaneCount}
                </p>
                <p className="text-xs text-orange-500 mt-1">
                  {orfaneFilter
                    ? "Clicca per mostrare tutte"
                    : "Clicca per filtrare"}
                </p>
              </Card>
            ) : null;
          })()}
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anno
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={annoFilter}
              onChange={(e) => handleFilterChange("anno", e.target.value)}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RSA
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={rsaIdFilter}
              onChange={(e) => handleFilterChange("rsaId", e.target.value)}
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((rsa) => (
                <option key={rsa.id} value={rsa.id}>
                  {rsa.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo utenza
            </label>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={tipoFilter}
              onChange={(e) => handleFilterChange("tipoutenza", e.target.value)}
            >
              <option value="">Tutti i tipi</option>
              {Object.entries(TIPO_UTENZA_OPTIONS).map(([value, opt]) => (
                <option key={value} value={value}>
                  {opt.icon} {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setSearchParams({ anno: annoFilter })}
            >
              Resetta filtri
            </Button>
          </div>
        </div>
        {/* Export Excel row */}
        {canCreate && (
          <div className="flex items-end gap-4 mt-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Export dal
              </label>
              <input
                type="date"
                className="border rounded-md px-3 py-2"
                value={exportDa}
                onChange={(e) => setExportDa(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                al
              </label>
              <input
                type="date"
                className="border rounded-md px-3 py-2"
                value={exportA}
                onChange={(e) => setExportA(e.target.value)}
              />
            </div>
            <Button
              onClick={handleExportExcel}
              disabled={exporting}
              variant="outline"
            >
              {exporting ? "Generazione..." : "Esporta Excel"}
            </Button>
            <Button
              onClick={handleExportPdf}
              disabled={exportingPdf}
              variant="outline"
            >
              {exportingPdf ? "Download PDF..." : "Scarica PDF"}
            </Button>
          </div>
        )}
        {/* Report consumi per mese (richieste dei proprietari degli immobili) */}
        {canCreate && (
          <div className="flex items-end gap-4 mt-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Immobile (report proprietario)
              </label>
              <select
                className="border rounded-md px-3 py-2 min-w-[240px]"
                value={exportImmobileId}
                onChange={(e) => setExportImmobileId(e.target.value)}
                disabled={!rsaIdFilter}
              >
                <option value="">
                  {rsaIdFilter
                    ? "Tutti gli immobili della RSA"
                    : "Seleziona prima una RSA"}
                </option>
                {exportImmobiliList.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.nome}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleExportConsumiMensili}
              disabled={exportingMensile}
              variant="outline"
            >
              {exportingMensile ? "Generazione..." : "Report consumi per mese"}
            </Button>
            <p className="text-xs text-gray-500 pb-2 max-w-md">
              Tabella tipologia utenza x mese sul periodo scelto sopra, un
              foglio per immobile. Formato pensato per le richieste dati dei
              proprietari.
            </p>
          </div>
        )}
      </Card>

      {/* Table */}
      <Card>
        <DataTable
          columns={columns}
          data={consumi}
          loading={loading}
          emptyMessage="Nessuna bolletta trovata"
          onRowClick={(row) => navigate(`/consumi-utenze/${row.id}`)}
        />
      </Card>

      {/* PDF Upload Modal */}
      <Modal
        isOpen={showPdfModal}
        onClose={closePdfModal}
        title={
          pdfPhase === "upload"
            ? "Carica Bollette PDF"
            : pdfPhase === "summary"
              ? "Riepilogo importazione"
              : "Verifica dati estratti"
        }
        size={pdfPhase === "review" ? "xl" : "md"}
      >
        {pdfPhase === "upload" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Seleziona uno o piu' PDF di bollette. Il sistema li analizzera'
              uno alla volta permettendoti di rivedere i dati estratti.
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) =>
                  setPdfFiles(e.target.files ? Array.from(e.target.files) : [])
                }
                className="hidden"
                id="pdf-upload-input"
              />
              <label
                htmlFor="pdf-upload-input"
                className="cursor-pointer block"
              >
                {pdfFiles.length > 0 ? (
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      {pdfFiles.length === 1
                        ? pdfFiles[0].name
                        : `${pdfFiles.length} file selezionati`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {pdfFiles.length === 1
                        ? `${(pdfFiles[0].size / 1024).toFixed(0)} KB`
                        : pdfFiles.map((f) => f.name).join(", ")}
                    </p>
                    <p className="text-sm text-blue-600 mt-2">
                      Clicca per cambiare selezione
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-500 text-lg mb-2">
                      Trascina qui o clicca per selezionare
                    </p>
                    <p className="text-sm text-gray-400">
                      Uno o piu' file PDF, max 20MB ciascuno
                    </p>
                  </div>
                )}
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={closePdfModal}>
                Annulla
              </Button>
              <Button
                onClick={handlePdfUpload}
                disabled={pdfFiles.length === 0 || uploading}
              >
                {uploading
                  ? "Analisi in corso..."
                  : pdfFiles.length > 1
                    ? `Analizza ${pdfFiles.length} PDF`
                    : "Analizza PDF"}
              </Button>
            </div>
          </div>
        )}

        {pdfPhase === "review" && pdfResult && (
          <div className="space-y-5">
            {/* Confidence & file info */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                File: <span className="font-medium">{pdfResult.fileName}</span>
                {pdfResult.templateName && (
                  <span className="ml-2 text-xs text-green-600">
                    (Template: {pdfResult.templateName})
                  </span>
                )}
                {pdfResult.ocrUsed && (
                  <span className="ml-2 text-xs text-blue-600">(OCR)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {pdfFiles.length > 1 && (
                  <Badge variant="info">
                    {currentPdfIndex + 1} di {pdfFiles.length}
                  </Badge>
                )}
                {getConfidenceBadge(pdfResult.confidence)}
              </div>
            </div>

            {/* RSA name detected from invoice (waste/no-POD invoices) */}
            {pdfResult.parsed?.rsaName && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">
                    RSA rilevata dalla fattura:
                  </span>{" "}
                  {pdfResult.parsed.rsaName}
                  {pdfResult.parsed.codiceCliente && (
                    <span className="ml-2 text-blue-600">
                      (Cod. Cliente: {pdfResult.parsed.codiceCliente})
                    </span>
                  )}
                  {pdfResult.parsed.cerCode && (
                    <span className="ml-2 text-blue-600">
                      CER: {pdfResult.parsed.cerCode}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Warnings */}
            {pdfResult.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm font-medium text-amber-800 mb-1">
                  Avvisi:
                </p>
                <ul className="text-sm text-amber-700 list-disc list-inside">
                  {pdfResult.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Errors */}
            {pdfResult.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm font-medium text-red-800 mb-1">
                  Errori parsing:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside">
                  {pdfResult.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Collegamento */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Collegamento
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                  label="RSA"
                  required
                  value={pdfForm.rsaId}
                  onChange={(e) => handlePdfRsaChange(e.target.value)}
                  options={rsaList.map((r) => ({
                    value: r.id,
                    label: r.nome,
                  }))}
                  placeholder="Seleziona RSA..."
                />
                <Select
                  label="Fornitore"
                  required
                  value={pdfForm.fornitoreId}
                  onChange={(e) =>
                    handlePdfFormChange("fornitoreId", e.target.value)
                  }
                  options={fornitoriList.map((f) => ({
                    value: f.id,
                    label: f.nome,
                  }))}
                  placeholder="Seleziona Fornitore..."
                />
                <Select
                  label="Immobile"
                  value={pdfForm.immobileId}
                  onChange={(e) =>
                    handlePdfFormChange("immobileId", e.target.value)
                  }
                  options={immobiliList.map((i) => ({
                    value: i.id,
                    label: i.nome,
                  }))}
                  placeholder="Seleziona Immobile..."
                  disabled={!pdfForm.rsaId}
                />
                <Select
                  label="Contatore"
                  value={pdfForm.contatoreId}
                  onChange={(e) =>
                    handlePdfFormChange("contatoreId", e.target.value)
                  }
                  options={contatoriList.map((c) => ({
                    value: c.id,
                    label:
                      `${c.nome || ""} ${c.codice ? `(${c.codice})` : ""}`.trim(),
                  }))}
                  placeholder="Seleziona Contatore..."
                  disabled={!pdfForm.rsaId}
                />
              </div>
            </div>

            {/* Tipo e POD */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Tipo e identificativi
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select
                  label="Tipo utenza"
                  value={pdfForm.tipoutenza}
                  onChange={(e) =>
                    handlePdfFormChange("tipoutenza", e.target.value)
                  }
                  options={Object.entries(TIPO_UTENZA_OPTIONS).map(
                    ([value, opt]) => ({
                      value,
                      label: `${opt.icon} ${opt.label}`,
                    }),
                  )}
                  placeholder="Seleziona tipo..."
                />
                <Input
                  label={
                    pdfForm.tipoutenza === "100000005"
                      ? "Cod. Cliente"
                      : "POD/PDR/Cod. Utenza"
                  }
                  value={pdfForm.pod}
                  onChange={(e) => handlePdfFormChange("pod", e.target.value)}
                />
                <Input
                  label="Numero fattura"
                  value={pdfForm.numeroFattura}
                  onChange={(e) =>
                    handlePdfFormChange("numeroFattura", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Importi */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Importi
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Input
                  label="Totale"
                  required
                  type="number"
                  step="0.01"
                  value={pdfForm.importoTotale}
                  onChange={(e) =>
                    handlePdfFormChange("importoTotale", e.target.value)
                  }
                />
                <Input
                  label="Materia prima"
                  type="number"
                  step="0.01"
                  value={pdfForm.importoEnergia}
                  onChange={(e) =>
                    handlePdfFormChange("importoEnergia", e.target.value)
                  }
                />
                <Input
                  label="Rete/Trasporto"
                  type="number"
                  step="0.01"
                  value={pdfForm.importoRete}
                  onChange={(e) =>
                    handlePdfFormChange("importoRete", e.target.value)
                  }
                />
                <Input
                  label="Imposte"
                  type="number"
                  step="0.01"
                  value={pdfForm.importoImposte}
                  onChange={(e) =>
                    handlePdfFormChange("importoImposte", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Periodo e consumi */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Periodo e consumi
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Input
                  label="Periodo inizio"
                  type="date"
                  value={pdfForm.periodoInizio}
                  onChange={(e) =>
                    handlePdfFormChange("periodoInizio", e.target.value)
                  }
                />
                <Input
                  label="Periodo fine"
                  type="date"
                  value={pdfForm.periodoFine}
                  onChange={(e) =>
                    handlePdfFormChange("periodoFine", e.target.value)
                  }
                />
                <Input
                  label="Data emissione"
                  type="date"
                  value={pdfForm.dataEmissione}
                  onChange={(e) =>
                    handlePdfFormChange("dataEmissione", e.target.value)
                  }
                />
                <Input
                  label="Data scadenza"
                  type="date"
                  value={pdfForm.dataScadenza}
                  onChange={(e) =>
                    handlePdfFormChange("dataScadenza", e.target.value)
                  }
                />
                <Input
                  label="Consumo"
                  type="number"
                  step="0.01"
                  value={pdfForm.consumoQuantita}
                  onChange={(e) =>
                    handlePdfFormChange("consumoQuantita", e.target.value)
                  }
                />
                <Input
                  label="Unita di misura"
                  value={pdfForm.consumoUnita}
                  onChange={(e) =>
                    handlePdfFormChange("consumoUnita", e.target.value)
                  }
                  placeholder="kWh, Smc, m3..."
                />
              </div>
            </div>

            {/* Stato e note */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Stato e note
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                  label="Stato pagamento"
                  value={pdfForm.statoPagamento}
                  onChange={(e) =>
                    handlePdfFormChange("statoPagamento", e.target.value)
                  }
                  options={Object.entries(STATO_PAGAMENTO_OPTIONS).map(
                    ([value, opt]) => ({
                      value,
                      label: opt.label,
                    }),
                  )}
                />
                <Textarea
                  label="Note"
                  rows={pdfForm.note?.includes("Ritiri:") ? 5 : 2}
                  value={pdfForm.note}
                  onChange={(e) => handlePdfFormChange("note", e.target.value)}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end items-center gap-3 pt-2 border-t">
              {pdfFiles.length > 1 && (
                <Button
                  variant="outline"
                  onClick={handlePdfSkip}
                  disabled={saving || uploading}
                >
                  Salta
                </Button>
              )}
              <Button
                variant="outline"
                onClick={closePdfModal}
                disabled={saving || uploading}
              >
                {pdfFiles.length > 1 ? "Annulla tutto" : "Annulla"}
              </Button>
              <Button onClick={handlePdfSave} disabled={saving || uploading}>
                {saving || uploading
                  ? "Elaborazione..."
                  : currentPdfIndex < pdfFiles.length - 1
                    ? "Salva e Prossima"
                    : "Salva Bolletta"}
              </Button>
            </div>
          </div>
        )}

        {pdfPhase === "summary" && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <p className="text-lg font-semibold text-gray-900">
                Importazione completata
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {bulkResults.filter((r) => r.status === "saved").length} salvate
                su {bulkResults.length} bollette
              </p>
            </div>
            <div className="divide-y">
              {bulkResults.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <div className="min-w-0 mr-3">
                    <span className="text-sm text-gray-700 truncate block">
                      {r.fileName}
                    </span>
                    {r.status === "duplicate" && (
                      <span className="text-xs text-gray-400 truncate block">
                        {r.error}
                      </span>
                    )}
                  </div>
                  <Badge
                    variant={
                      r.status === "saved"
                        ? "success"
                        : r.status === "skipped"
                          ? "warning"
                          : r.status === "duplicate"
                            ? "info"
                            : "danger"
                    }
                  >
                    {r.status === "saved"
                      ? "Salvata"
                      : r.status === "skipped"
                        ? "Saltata"
                        : r.status === "duplicate"
                          ? "Duplicata"
                          : "Errore"}
                  </Badge>
                </div>
              ))}
            </div>
            <Button onClick={closePdfModal} className="w-full">
              Chiudi
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ConsumiUtenzeListPage;
